import {useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {isAnalysisToolRedesignEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {
  AnalysisGraphQL,
  AnalysisViewLineageNode,
  GetAnalysisPipelineStep,
  isParameterOptimizationStepType,
} from 'analysis-tool/AnalysisTypes';
import {getLegacyNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {
  assertViewExists,
  getMainViewItemForLineageNode,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  isViewStepConfigAnalysisItem,
  STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE,
  StepAwareAnalysisMainViewItemType,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  getDatasetSidebarItemData,
  getSaveToResultsSidebarItemData,
  getParameterOptimizationConfigurationSidebarItemData,
  getExportDataCatalogDatasetSidebarItemData,
  getSendToDocumentSidebarItemData,
} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert} from 'Asserts';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import useRightSidebarItem from './useRightSidebarItem';

/**
 * Global state tracking what item is currently selected for an analysis if any.
 *
 * The state can contain either a concrete item or null. Initially, it is null and we map that to a concrete
 * item in the hook body utilizing the selectedItem. We expose that concrete item as the "selected item"
 * to the hook caller. This allows for an initial item value that is dynamic based on the analysis rather than
 * always being something like EMPTY_ANALYSIS.
 *
 * @package
 */
export const selectedItemGlobalState = makeUseGlobalClientState<AnalysisMainViewItem | null>(null);

/**
 * For a selected item that is a config type, return if it is a valid item for the step.
 */
function isViewStepConfigSelectedItemValidForStep(
  selectedItem: AnalysisMainViewItem,
  step: GetAnalysisPipelineStep
): boolean {
  if (!isViewStepConfigAnalysisItem(selectedItem)) {
    return false;
  }
  return (
    STEP_TYPE_TO_VIEW_STEP_CONFIG_VIEW_TYPE[step.definition.stepType] === selectedItem.type &&
    step.config != null
  );
}

function getValidSelectedItemForDatasetStep(
  analysis: AnalysisGraphQL,
  internalSelectedItem: AnalysisMainViewItem | null,
  selectedStep: GetAnalysisPipelineStep,
  dataFrameId?: string
): AnalysisMainViewItem {
  let selectedData;

  const outputDatasetStepDatas = selectedStep.outputStepDatas.filter(
    (stepData) => stepData.kind === PipelineStepDataKind.DATASET
  );

  if (outputDatasetStepDatas.length >= 1) {
    if (
      internalSelectedItem != null &&
      internalSelectedItem.type === AnalysisMainViewItemType.DATA_FRAME &&
      [PipelineStepStatusCode.RUNNING, PipelineStepStatusCode.BLOCKED].includes(selectedStep.statusCode)
    ) {
      return internalSelectedItem;
    } else if (outputDatasetStepDatas.some((stepData) => stepData.dataFrames.length > 0)) {
      // If there are any output step datas with data frames, then we should always select one of them.
      // If any contain the provided dataFrameId, then return that one.
      // Otherwise, fall back to the first one with any data frame.
      selectedData = outputDatasetStepDatas
        .flatMap((stepData) =>
          stepData.dataFrames.map((dataFrame) => ({outputStepData: stepData, dataFrame}))
        )
        .find(({dataFrame}) => dataFrame.id === dataFrameId);
      if (selectedData == null) {
        const firstOutputStepDataWithDataFrames = outputDatasetStepDatas.find(
          (stepData) => stepData.dataFrames.length > 0
        )!;
        selectedData = {
          outputStepData: firstOutputStepDataWithDataFrames,
          dataFrame: firstOutputStepDataWithDataFrames.dataFrames[0],
        };
      }
    }
  }

  return {
    // If both the internal selected item and the dataset sidebar item are null, then display an empty analysis view
    type: AnalysisMainViewItemType.EMPTY_ANALYSIS,
    ...internalSelectedItem,
    ...getDatasetSidebarItemData(analysis, {
      step: selectedStep,
      ...(selectedData != null ? {data: selectedData} : {}),
    })?.mainViewItem,
  };
}

/**
 * Return a valid selected item based on `internalSelectedItem`.
 *
 * Returned `selectedItem` is determined by evaluating `internalSelectedItem`'s validity in these case:
 * - `internalSelectedItem` is `null` - return the analysis's default item.
 * - `internalSelectedItem` is not step-aware, this is always valid, return it.
 * - `internalSelectedItem` is step-aware.
 *    - The selected step is not in the analysis - return the analysis's default item.
 *    - The selected item type is `RUNNING` but the step has completed - return the analysis's default item.
 *    - The selected view item is a valid configuration view for the selected step - return the item as is
 *    - The selected step corresponds to a top-level file - return item as is.
 *    - The selected step corresponds to a top-level dataset.
 *      - If the item is valid for that step, return it as is.
 *      - Otherwise (e.g. item is `FAILED`, but step is `RUNNING`), return the correct item.
 *    - The selected step corresponds to an ancestor of a top-level dataset.
 *      - Return the correct view item for the last step in that top-level dataset's lineage.
 *
 * @see getLegacyDefaultMainViewItemForAnalysis for details on what the analysis's default item is.
 * @deprecated
 * @see useLegacySelectedAnalysisItem for deprecation details
 */
function getLegacyValidSelectedItem(
  internalSelectedItem: AnalysisMainViewItem | null,
  analysis: AnalysisGraphQL
): AnalysisMainViewItem {
  if (internalSelectedItem == null) {
    return getDefaultMainViewItemForAnalysis(analysis);
  } else if (isStepAwareAnalysisItem(internalSelectedItem)) {
    const selectedStep = getLegacyNullableStepFromAnalysis(analysis, internalSelectedItem.stepId);
    const dataFrameId =
      internalSelectedItem.type === AnalysisMainViewItemType.DATA_FRAME
        ? internalSelectedItem.dataFrameId
        : undefined;

    if (selectedStep == null) {
      return getDefaultMainViewItemForAnalysis(analysis);
    } else {
      if (
        internalSelectedItem.type === AnalysisMainViewItemType.RUNNING &&
        [PipelineStepStatusCode.FAILED, PipelineStepStatusCode.SUCCEEDED].includes(selectedStep.statusCode) &&
        !selectedStep.outputStepDatas.some(
          (stepData) => stepData.dataFrames?.length > 0 || stepData.scratchFiles?.length > 0
        )
      ) {
        return getDefaultMainViewItemForAnalysis(analysis);
      }

      if (selectedStep.definition.stepType === PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION) {
        return getParameterOptimizationConfigurationSidebarItemData(analysis, selectedStep).mainViewItem;
      }

      if (
        isParameterOptimizationStepType(selectedStep.definition.stepType) ||
        internalSelectedItem.type === AnalysisMainViewItemType.FILE ||
        selectedStep.definition.stepType === PipelineStepType.REGRESSION
      ) {
        return internalSelectedItem;
      }

      if (isViewStepConfigSelectedItemValidForStep(internalSelectedItem, selectedStep)) {
        return internalSelectedItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
        return getSaveToResultsSidebarItemData(selectedStep).mainViewItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
        return getExportDataCatalogDatasetSidebarItemData(selectedStep).mainViewItem;
      }

      if (selectedStep.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT) {
        return getSendToDocumentSidebarItemData(selectedStep).mainViewItem;
      }

      return getValidSelectedItemForDatasetStep(analysis, internalSelectedItem, selectedStep, dataFrameId);
    }
  }

  return internalSelectedItem;
}

/**
 * @deprecated
 * @package
 */
export const legacySelectedItemGlobalState = makeUseGlobalClientState<AnalysisMainViewItem | null>(null);

/**
 * Hook to capture an item selected for display in the main view of the Analysis UI
 *
 * @deprecated This describe's `useSelectedAnalysisItem` behavior when `isAnalysisToolRedesignEnabled()` is off.
 * @see useSelectedAnalysisItem for details on the new behavior.
 */
function useLegacySelectedAnalysisItem(
  clientStateId: string,
  analysis: AnalysisGraphQL
): UseSelectedAnalysisItemOutput {
  const [initialSelectedItem, setInitialSelectedItem] =
    legacySelectedItemGlobalState.useGlobalClientState(clientStateId);
  const selectedItem = getLegacyValidSelectedItem(initialSelectedItem, analysis);
  useEffect(() => {
    if (!isEqual(initialSelectedItem, selectedItem)) {
      setInitialSelectedItem(selectedItem);
    }
  }, [initialSelectedItem, selectedItem, setInitialSelectedItem]);

  return {
    selectedItem,
    onChangeSelectedItem: (item: AnalysisMainViewItem | null) => {
      setInitialSelectedItem(item);
    },
  };
}

/**
 * Return a valid selected item based on `selectedItem`.
 *
 * Returned `selectedItem` is determined by evaluating `selectedItem`'s validity in these case:
 * - `selectedItem` is `null` - return the analysis's default item.
 * - `selectedItem` is not step-aware, this is always valid, return it.
 * - `selectedItem` is step-aware.
 *    - The selected step is not in the analysis - return the analysis's default item.
 *    - Otherwise, this is always valid, return it.
 *
 * @see getDefaultMainViewItemForAnalysis for details on what the analysis's default item is.
 */
function getValidSelectedItem(
  analysis: AnalysisGraphQL,
  selectedItem: AnalysisMainViewItem | null
): AnalysisMainViewItem {
  if (selectedItem == null) {
    return getDefaultMainViewItemForAnalysis(analysis);
  }

  if (isStepAwareAnalysisItem(selectedItem)) {
    const step = getLegacyNullableStepFromAnalysis(analysis, selectedItem.stepId);
    if (step == null) {
      return getDefaultMainViewItemForAnalysis(analysis);
    }
  }

  return selectedItem;
}

/**
 * Return the next selected `AnalysisMainViewItem` after deleting steps when the current item is a `DATA_VIEW`.
 *
 * When the selected item is a `DATA_VIEW`, and we delete a step, that selected item might become invalid. We
 * use the following logic to determine the next selected item:
 *  - If the step referenced by the selected item isn't being delete, we don't need to update the selected item,
 * so we return it.
 *  - If the step referenced by the selected item is being deleted, we select the last remaining step in the view.
 *  - If the entire view is being deleted, we select the first parent view.
 *  - If there's no remaining parent view, return `null` to allow the analysis to display it's default view.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps when the current item is a `DATA_VIEW`.
 * @package
 */
export function getNextSelectedAnalysisItemForView(
  analysis: AnalysisGraphQL,
  selectedItem: AnalysisMainViewItem<AnalysisMainViewItemType.DATA_VIEW>,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  const selectedViewReference = selectedItem.viewReference;
  const view = getViewForViewReference(analysis, selectedViewReference);

  assertViewExists(view, analysis, selectedViewReference);
  assert(view.lineage.length > 0, 'Expected view lineage to not be empty');

  const remainingLineageNodes = view.lineage.filter(
    ({step}: AnalysisViewLineageNode) => !stepsToDelete.has(step.id)
  );

  // If the selected item isn't being deleted, we don't need to update the selected item, so return null
  if (remainingLineageNodes.some(({step}) => step.id === selectedViewReference.stepId)) {
    return selectedItem;
    // Otherwise, select the last remaining node in the view
  } else if (remainingLineageNodes.length > 0) {
    const lastRemainingNode = remainingLineageNodes[remainingLineageNodes.length - 1];
    return getMainViewItemForLineageNode(lastRemainingNode);
  }

  // Otherwise, the view is getting completely deleted, so see if there's a valid parent view to select
  const firstNode = view.lineage[0];
  if (firstNode.step.incomingEdges.length > 0) {
    const firstParentEdge = firstNode.step.incomingEdges[0];

    return {
      type: AnalysisMainViewItemType.DATA_VIEW,
      viewReference: {
        stepId: firstParentEdge.fromStep.id,
        outputReference: {
          stepDataName: firstParentEdge.fromStepData.name,
          rawDataIndex: firstParentEdge.fromStepDataIndex,
        },
      },
    };
  }

  return null;
}

/**
 * Return the next selected `AnalysisMainViewItem` after deleting steps when the current item is step-aware.
 *
 * If the step referenced by the selected item is being deleted, return `null` to allow the analysis to display
 * it's default view. Otherwise, we return the selected item.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps when the current item is step-aware.
 * @package
 */
export function getNextSelectedAnalysisItemForStepAware(
  selectedItem: AnalysisMainViewItem<StepAwareAnalysisMainViewItemType>,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  if (stepsToDelete.has(selectedItem.stepId)) {
    return null;
  }

  return selectedItem;
}

/**
 * Return the `AnalysisMainViewItem` that should be selected after the delete operations.
 *
 * The selected item might reference a step that is being deleted, so it will become invalid after the
 * delete operation.
 *
 * @returns The next selected `AnalysisMainViewItem` after deleting steps.
 * @package
 */
export function getNextSelectedAnalysisItem(
  analysis: AnalysisGraphQL,
  selectedItem: AnalysisMainViewItem | null,
  stepsToDelete: Set<string>
): AnalysisMainViewItem | null {
  if (selectedItem == null) {
    return selectedItem;
  } else if (selectedItem.type === AnalysisMainViewItemType.DATA_VIEW) {
    return getNextSelectedAnalysisItemForView(analysis, selectedItem, stepsToDelete);
  } else if (isStepAwareAnalysisItem(selectedItem)) {
    return getNextSelectedAnalysisItemForStepAware(selectedItem, stepsToDelete);
  }

  return selectedItem;
}

/**
 * Updates selected main view item after deleting analysis steps to maintain valid state.
 *
 * - This function reads the selected `AnalysisMainViewItem` from the client, and then it checks if it
 * needs to be updated after the delete operation.
 * - If it needs to be updated, it updates the client state and stores the previous values.
 * - If it doesn't need to be updated, it doesn't update the client state and doesn't store the previous values.
 * - If the backend deletion call fails, it rolls back the client state to the previous values if it was updated.
 */
export async function updateSelectedAnalysisItemForDelete(
  deleteFunction: () => Promise<void>,
  analysis: AnalysisGraphQL,
  clientStateId: string,
  stepsToDelete: Set<string>
): Promise<void> {
  const selectedItem = selectedItemGlobalState.getState(clientStateId);
  let previousSelectedItem: AnalysisMainViewItem | null | undefined;

  const nextSelectedItem = getNextSelectedAnalysisItem(analysis, selectedItem, stepsToDelete);
  if (!isEqual(nextSelectedItem, selectedItem)) {
    selectedItemGlobalState.updateState(clientStateId, nextSelectedItem);
    previousSelectedItem = selectedItem;
  }

  try {
    await deleteFunction();
  } catch (error) {
    const selectedItem = selectedItemGlobalState.getState(clientStateId);
    if (previousSelectedItem !== undefined && !isEqual(selectedItem, previousSelectedItem)) {
      selectedItemGlobalState.updateState(clientStateId, previousSelectedItem);
    }
    throw error;
  }
}

export interface UseSelectedAnalysisItemOutput {
  selectedItem: AnalysisMainViewItem;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
}

/**
 * Hook to capture an item selected for display in the main view of the Analysis UI.
 */
export default function useSelectedAnalysisItem(
  clientStateId: string,
  analysis: AnalysisGraphQL
): UseSelectedAnalysisItemOutput {
  const [maybeSelectedItem, setSelectedItem] = selectedItemGlobalState.useGlobalClientState(clientStateId);

  const selectedItem = getValidSelectedItem(analysis, maybeSelectedItem);
  const {clearRightSidebarItem} = useRightSidebarItem(clientStateId);

  // Pass through the legacy hook's behavior if the feature flag is off. We can't optionally render hooks
  // in react, so need to render both and choose an output to return.
  const legacySelectedItemHookOutputs = useLegacySelectedAnalysisItem(clientStateId, analysis);
  if (!isAnalysisToolRedesignEnabled()) {
    return legacySelectedItemHookOutputs;
  }

  return {
    selectedItem,
    onChangeSelectedItem: (item: AnalysisMainViewItem | null) => {
      setSelectedItem(item);
      clearRightSidebarItem();
    },
  };
}
